import {
  preflightPdfStructure,
  type PdfStructuralInspection,
  type PdfStructuralLimits,
} from '@/shared/lib/pdf/structuralPreflight'

export const PDF_VIEWER_MAX_FILE_BYTES = 32 * 1024 * 1024
export const PDF_VIEWER_MAX_PAGES = 400
export const PDF_VIEWER_MAX_CANVAS_PIXELS = 24_000_000
export const PDF_VIEWER_MAX_CANVAS_SIDE = 8_192
export const PDF_VIEWER_LOAD_TIMEOUT_MS = 15_000
export const PDF_VIEWER_RENDER_TIMEOUT_MS = 12_000

const PDF_VIEWER_STRUCTURAL_LIMITS: PdfStructuralLimits = {
  fileBytes: PDF_VIEWER_MAX_FILE_BYTES,
  indirectObjects: 25_000,
  incrementalUpdates: 16,
  declaredObjectSlots: 100_000,
  objectStreams: 256,
  objectsPerStream: 2_000,
  declaredStreamBytes: PDF_VIEWER_MAX_FILE_BYTES,
  aggregateDeclaredStreamBytes: 128 * 1024 * 1024,
  dictionaryBytes: 8_192,
  nestingDepth: 32,
}
const MAX_IMAGES = 2_000
const MAX_IMAGE_SIDE = 32_768
const MAX_IMAGE_PIXELS = 64_000_000
const MAX_AGGREGATE_IMAGE_PIXELS = 512_000_000
const MAX_FILTER_CHAIN = 2
const MAX_DECODED_STREAM_BYTES = 16 * 1024 * 1024
const MAX_AGGREGATE_DECODED_BYTES = 32 * 1024 * 1024
const MAX_CONTENT_OPERATORS = 2_000_000
const MAX_GRAPHICS_STACK = 256
const MAX_INLINE_IMAGES = 1_000
const MAX_AGGREGATE_IMAGE_BYTES = 128 * 1024 * 1024

interface ViewerResourceUsage {
  imageCount: number
  imagePixels: number
  imageBytes: number
}

export type PdfViewerErrorCode =
  | 'empty'
  | 'unsupported'
  | 'too_large'
  | 'corrupt'
  | 'encrypted'
  | 'no_pages'
  | 'too_many_pages'
  | 'unsafe_structure'
  | 'page_too_large'
  | 'canvas_unavailable'
  | 'timeout'
  | 'render_failed'

export class PdfViewerError extends Error {
  constructor(
    public readonly code: PdfViewerErrorCode,
    message: string = code,
  ) {
    super(message)
    this.name = 'PdfViewerError'
  }
}

interface PdfViewport {
  width: number
  height: number
}

interface PdfRenderTask {
  promise: Promise<void>
  cancel: () => void
}

interface PdfPage {
  getViewport: (options: { scale: number }) => PdfViewport
  render: (options: {
    canvasContext: CanvasRenderingContext2D
    viewport: PdfViewport
  }) => PdfRenderTask
  cleanup: () => void
}

interface PdfDocument {
  numPages: number
  getPage: (pageNumber: number) => Promise<PdfPage>
}

interface PdfLoadingTask {
  promise: Promise<PdfDocument>
  destroy: () => Promise<void>
}

export interface PdfJsViewerModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (source: { data: Uint8Array }) => PdfLoadingTask
}

export interface PdfViewerSession {
  pageCount: number
  renderPage: (
    pageNumber: number,
    scale: number,
    canvas: HTMLCanvasElement,
    signal: AbortSignal,
  ) => Promise<{ width: number; height: number }>
  close: () => Promise<void>
}

export interface PdfViewerRuntimeBudgets {
  loadTimeoutMs?: number
  renderTimeoutMs?: number
}

export function validatePdfViewerFile(file: Pick<File, 'name' | 'size' | 'type'>): PdfViewerError | null {
  if (file.size === 0) return new PdfViewerError('empty')
  if (file.size > PDF_VIEWER_MAX_FILE_BYTES) return new PdfViewerError('too_large')

  const filenameIsPdf = file.name.toLowerCase().endsWith('.pdf')
  const mimeIsPdf = file.type === 'application/pdf'
  if (!filenameIsPdf && !mimeIsPdf) return new PdfViewerError('unsupported')
  return null
}

export async function openPdfViewer(
  file: File,
  signal: AbortSignal,
  moduleOverride?: PdfJsViewerModule,
  runtimeBudgets: PdfViewerRuntimeBudgets = {},
): Promise<PdfViewerSession> {
  const validationError = validatePdfViewerFile(file)
  if (validationError) throw validationError

  const bytes = new Uint8Array(await file.arrayBuffer())
  throwIfAborted(signal)
  if (!hasPdfSignature(bytes)) throw new PdfViewerError('corrupt')
  const inspection = preflightPdfViewerBytes(bytes)
  await enforceDecodedResourcePolicy(bytes, inspection)
  throwIfAborted(signal)

  const pdfjs = moduleOverride ?? await loadPdfJs()
  throwIfAborted(signal)
  const loadingTask = pdfjs.getDocument({ data: bytes })
  let closed = false
  let closePromise: Promise<void> | null = null
  const close = () => {
    closePromise ??= loadingTask.destroy().catch(() => {})
    closed = true
    return closePromise
  }
  const onAbort = () => { void close() }
  signal.addEventListener('abort', onAbort, { once: true })

  let document: PdfDocument
  try {
    document = await withDeadline(
      loadingTask.promise,
      signal,
      runtimeBudgets.loadTimeoutMs ?? PDF_VIEWER_LOAD_TIMEOUT_MS,
      close,
    )
  } catch (error) {
    signal.removeEventListener('abort', onAbort)
    await close()
    throw mapPdfViewerError(error)
  }
  signal.removeEventListener('abort', onAbort)
  throwIfAborted(signal)

  if (!Number.isSafeInteger(document.numPages) || document.numPages < 1) {
    await close()
    throw new PdfViewerError('no_pages')
  }
  if (document.numPages > PDF_VIEWER_MAX_PAGES) {
    await close()
    throw new PdfViewerError('too_many_pages')
  }

  return {
    pageCount: document.numPages,
    async renderPage(pageNumber, scale, canvas, renderSignal) {
      if (closed) throw new DOMException('Session closed', 'AbortError')
      throwIfAborted(renderSignal)
      if (!Number.isSafeInteger(pageNumber) || pageNumber < 1 || pageNumber > document.numPages) {
        throw new PdfViewerError('render_failed')
      }
      if (!Number.isFinite(scale) || scale <= 0 || scale > 3) {
        throw new PdfViewerError('render_failed')
      }

      const renderTimeoutMs = runtimeBudgets.renderTimeoutMs ?? PDF_VIEWER_RENDER_TIMEOUT_MS
      const renderStartedAt = performance.now()
      let page: PdfPage
      try {
        page = await withDeadline(
          document.getPage(pageNumber),
          renderSignal,
          renderTimeoutMs,
          close,
        )
      } catch (error) {
        if (renderSignal.aborted) throw new DOMException('Aborted', 'AbortError')
        throw mapPdfViewerError(error, 'render_failed')
      }
      try {
        throwIfAborted(renderSignal)
        const viewport = page.getViewport({ scale })
        const width = Math.ceil(viewport.width)
        const height = Math.ceil(viewport.height)
        if (
          !Number.isSafeInteger(width) ||
          !Number.isSafeInteger(height) ||
          width < 1 ||
          height < 1 ||
          width > PDF_VIEWER_MAX_CANVAS_SIDE ||
          height > PDF_VIEWER_MAX_CANVAS_SIDE ||
          width * height > PDF_VIEWER_MAX_CANVAS_PIXELS
        ) {
          throw new PdfViewerError('page_too_large')
        }

        const context = canvas.getContext('2d', { alpha: false })
        if (!context) throw new PdfViewerError('canvas_unavailable')
        canvas.width = width
        canvas.height = height

        const renderTask = page.render({ canvasContext: context, viewport })
        try {
          await withDeadline(
            renderTask.promise,
            renderSignal,
            Math.max(1, renderTimeoutMs - (performance.now() - renderStartedAt)),
            () => renderTask.cancel(),
          )
        } catch (error) {
          if (renderSignal.aborted) throw new DOMException('Aborted', 'AbortError')
          throw mapPdfViewerError(error, 'render_failed')
        }
        throwIfAborted(renderSignal)
        return { width, height }
      } finally {
        page.cleanup()
      }
    },
    close,
  }
}

export function preflightPdfViewerBytes(bytes: Uint8Array): PdfStructuralInspection {
  try {
    const inspection = preflightPdfStructure(bytes, PDF_VIEWER_STRUCTURAL_LIMITS)
    enforceDeclaredResourcePolicy(inspection)
    return inspection
  } catch {
    throw new PdfViewerError('unsafe_structure')
  }
}

function enforceDeclaredResourcePolicy(inspection: PdfStructuralInspection): ViewerResourceUsage {
  const usage: ViewerResourceUsage = { imageCount: 0, imagePixels: 0, imageBytes: 0 }
  for (const stream of inspection.streams) {
    if (stream.filters.length > MAX_FILTER_CHAIN || stream.filters.includes('__unsupported__')) {
      throw new PdfViewerError('unsafe_structure')
    }
    if (stream.typeIsIndirect || stream.subtypeIsIndirect) {
      throw new PdfViewerError('unsafe_structure')
    }
    if (stream.subtype !== 'Image') continue
    usage.imageCount += 1
    if (usage.imageCount > MAX_IMAGES) throw new PdfViewerError('unsafe_structure')
    const width = stream.width
    const height = stream.height
    if (
      width === undefined ||
      height === undefined ||
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width < 1 ||
      height < 1 ||
      width > MAX_IMAGE_SIDE ||
      height > MAX_IMAGE_SIDE
    ) {
      throw new PdfViewerError('unsafe_structure')
    }
    const pixels = width * height
    if (!Number.isSafeInteger(pixels) || pixels > MAX_IMAGE_PIXELS) {
      throw new PdfViewerError('unsafe_structure')
    }
    usage.imagePixels += pixels
    if (usage.imagePixels > MAX_AGGREGATE_IMAGE_PIXELS) {
      throw new PdfViewerError('unsafe_structure')
    }
  }
  return usage
}

async function enforceDecodedResourcePolicy(
  bytes: Uint8Array,
  inspection: PdfStructuralInspection,
): Promise<void> {
  const usage = enforceDeclaredResourcePolicy(inspection)
  let aggregateDecodedBytes = 0
  let operatorCount = 0
  let graphicsDepth = 0
  let inlineImages = 0
  for (const stream of inspection.streams) {
    if (stream.subtype === 'Image') continue
    const encoded = bytes.subarray(stream.dataStart, stream.dataStart + stream.length)
    let decoded: Uint8Array
    if (stream.filters.length === 0) {
      decoded = encoded
    } else if (
      stream.filters.length === 1 &&
      ['Fl', 'FlateDecode'].includes(stream.filters[0] ?? '')
    ) {
      decoded = await inflateBounded(encoded)
    } else {
      throw new PdfViewerError('unsafe_structure')
    }
    if (decoded.byteLength > MAX_DECODED_STREAM_BYTES) {
      throw new PdfViewerError('unsafe_structure')
    }
    aggregateDecodedBytes += decoded.byteLength
    if (aggregateDecodedBytes > MAX_AGGREGATE_DECODED_BYTES) {
      throw new PdfViewerError('unsafe_structure')
    }
    const inspected = inspectContentStream(
      new TextDecoder('latin1').decode(decoded),
      { operatorCount, graphicsDepth, inlineImages },
      usage,
    )
    operatorCount = inspected.operatorCount
    graphicsDepth = inspected.graphicsDepth
    inlineImages = inspected.inlineImages
  }
}

async function inflateBounded(encoded: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') throw new PdfViewerError('unsafe_structure')
  try {
    const body = new Response(encoded as BodyInit).body
    if (!body) throw new PdfViewerError('unsafe_structure')
    const stream = body.pipeThrough(new DecompressionStream('deflate'))
    const reader = stream.getReader()
    const chunks: Uint8Array[] = []
    let total = 0
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      total += value.byteLength
      if (total > MAX_DECODED_STREAM_BYTES) {
        await reader.cancel()
        throw new PdfViewerError('unsafe_structure')
      }
      chunks.push(value)
    }
    const output = new Uint8Array(total)
    let offset = 0
    for (const chunk of chunks) {
      output.set(chunk, offset)
      offset += chunk.byteLength
    }
    return output
  } catch (error) {
    if (error instanceof PdfViewerError) throw error
    throw new PdfViewerError('unsafe_structure')
  }
}

interface ContentToken {
  value: string
  start: number
  end: number
  kind: 'regular' | 'name' | 'ignored'
}

function nextContentToken(source: string, initialPosition: number): ContentToken | null {
  let position = initialPosition
  while (position < source.length) {
    const character = source[position]
    if (/[\0\t\n\f\r ]/.test(character ?? '')) {
      position += 1
      continue
    }
    if (character === '%') {
      while (position < source.length && !['\r', '\n'].includes(source[position] ?? '')) {
        position += 1
      }
      continue
    }
    if (character === '(') {
      const start = position
      position += 1
      let depth = 1
      while (position < source.length && depth > 0) {
        const next = source[position++]
        if (next === '\\') position += 1
        else if (next === '(') depth += 1
        else if (next === ')') depth -= 1
      }
      if (depth !== 0) throw new PdfViewerError('unsafe_structure')
      return { value: '', start, end: position, kind: 'ignored' }
    }
    if (character === '<' && source[position + 1] !== '<') {
      const start = position
      const end = source.indexOf('>', position + 1)
      if (end < 0) throw new PdfViewerError('unsafe_structure')
      position = end + 1
      return { value: '', start, end: position, kind: 'ignored' }
    }
    if (character === '/') {
      const start = position++
      while (
        position < source.length &&
        !/[\0\t\n\f\r ()<>[\]{}/%]/.test(source[position] ?? '')
      ) position += 1
      return { value: source.slice(start, position), start, end: position, kind: 'name' }
    }
    const start = position
    while (
      position < source.length &&
      !/[\0\t\n\f\r ()<>[\]{}/%]/.test(source[position] ?? '')
    ) {
      position += 1
    }
    if (start === position) {
      position += 1
      continue
    }
    return { value: source.slice(start, position), start, end: position, kind: 'regular' }
  }
  return null
}

function inlineInteger(entries: Map<string, string>, ...keys: string[]): number {
  const raw = keys.map((key) => entries.get(key)).find((value) => value !== undefined)
  if (raw === undefined || !/^\d+$/.test(raw)) throw new PdfViewerError('unsafe_structure')
  const value = Number(raw)
  if (!Number.isSafeInteger(value) || value < 1) throw new PdfViewerError('unsafe_structure')
  return value
}

function parseInlineImage(
  source: string,
  initialPosition: number,
  usage: ViewerResourceUsage,
): number {
  const entries = new Map<string, string>()
  let position = initialPosition
  while (true) {
    const key = nextContentToken(source, position)
    if (!key || key.kind !== 'name') throw new PdfViewerError('unsafe_structure')
    position = key.end
    const value = nextContentToken(source, position)
    if (!value || value.kind === 'ignored') throw new PdfViewerError('unsafe_structure')
    position = value.end
    if (key.value === '/ID') throw new PdfViewerError('unsafe_structure')
    if (value.kind === 'regular' && value.value === 'ID') {
      throw new PdfViewerError('unsafe_structure')
    }
    if (entries.has(key.value)) throw new PdfViewerError('unsafe_structure')
    entries.set(key.value, value.value)
    const marker = nextContentToken(source, position)
    if (!marker) throw new PdfViewerError('unsafe_structure')
    if (marker.kind === 'regular' && marker.value === 'ID') {
      position = marker.end
      break
    }
  }
  if (
    entries.has('/F') ||
    entries.has('/Filter') ||
    [...entries.keys()].some((key) => ![
      '/W', '/Width', '/H', '/Height', '/CS', '/ColorSpace', '/BPC', '/BitsPerComponent',
    ].includes(key))
  ) {
    throw new PdfViewerError('unsafe_structure')
  }
  const width = inlineInteger(entries, '/W', '/Width')
  const height = inlineInteger(entries, '/H', '/Height')
  const bits = inlineInteger(entries, '/BPC', '/BitsPerComponent')
  if (![1, 2, 4, 8, 16].includes(bits) || width > MAX_IMAGE_SIDE || height > MAX_IMAGE_SIDE) {
    throw new PdfViewerError('unsafe_structure')
  }
  const colorSpace = entries.get('/CS') ?? entries.get('/ColorSpace')
  const components = colorSpace === '/G' || colorSpace === '/DeviceGray'
    ? 1
    : colorSpace === '/RGB' || colorSpace === '/DeviceRGB'
      ? 3
      : colorSpace === '/CMYK' || colorSpace === '/DeviceCMYK'
        ? 4
        : 0
  if (components === 0) throw new PdfViewerError('unsafe_structure')
  const pixels = width * height
  const decodedBytes = Math.ceil(width * components * bits / 8) * height
  if (
    !Number.isSafeInteger(pixels) ||
    !Number.isSafeInteger(decodedBytes) ||
    pixels > MAX_IMAGE_PIXELS
  ) {
    throw new PdfViewerError('unsafe_structure')
  }
  usage.imageCount += 1
  usage.imagePixels += pixels
  usage.imageBytes += decodedBytes
  if (
    usage.imageCount > MAX_IMAGES ||
    usage.imagePixels > MAX_AGGREGATE_IMAGE_PIXELS ||
    usage.imageBytes > MAX_AGGREGATE_IMAGE_BYTES
  ) {
    throw new PdfViewerError('unsafe_structure')
  }
  if (!/[\0\t\n\f\r ]/.test(source[position] ?? '')) {
    throw new PdfViewerError('unsafe_structure')
  }
  if (source[position] === '\r' && source[position + 1] === '\n') position += 2
  else position += 1
  const dataEnd = position + decodedBytes
  if (dataEnd > source.length || !/[\0\t\n\f\r ]/.test(source[dataEnd] ?? '')) {
    throw new PdfViewerError('unsafe_structure')
  }
  const endToken = nextContentToken(source, dataEnd)
  if (!endToken || endToken.kind !== 'regular' || endToken.value !== 'EI') {
    throw new PdfViewerError('unsafe_structure')
  }
  return endToken.end
}

function inspectContentStream(
  source: string,
  state: { operatorCount: number; graphicsDepth: number; inlineImages: number },
  usage: ViewerResourceUsage,
): typeof state {
  let position = 0
  while (true) {
    const token = nextContentToken(source, position)
    if (!token) return state
    position = token.end
    if (token.kind !== 'regular') continue
    state.operatorCount += 1
    if (state.operatorCount > MAX_CONTENT_OPERATORS) throw new PdfViewerError('unsafe_structure')
    if (token.value === 'q') {
      state.graphicsDepth += 1
      if (state.graphicsDepth > MAX_GRAPHICS_STACK) throw new PdfViewerError('unsafe_structure')
    } else if (token.value === 'Q') {
      state.graphicsDepth = Math.max(0, state.graphicsDepth - 1)
    } else if (token.value === 'BI') {
      state.inlineImages += 1
      if (state.inlineImages > MAX_INLINE_IMAGES) throw new PdfViewerError('unsafe_structure')
      position = parseInlineImage(source, position, usage)
    }
  }
}

async function withDeadline<T>(
  promise: Promise<T>,
  signal: AbortSignal,
  timeoutMs: number,
  cancel: () => void | Promise<void>,
): Promise<T> {
  throwIfAborted(signal)
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let rejectAbort!: (reason: DOMException) => void
  const abort = new Promise<never>((_, reject) => { rejectAbort = reject })
  const onAbort = () => {
    void cancel()
    rejectAbort(new DOMException('Aborted', 'AbortError'))
  }
  signal.addEventListener('abort', onAbort, { once: true })
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      void cancel()
      reject(new PdfViewerError('timeout'))
    }, Math.max(1, timeoutMs))
  })
  try {
    return await Promise.race([promise, abort, timeout])
  } finally {
    signal.removeEventListener('abort', onAbort)
    clearTimeout(timeoutId)
  }
}

function hasPdfSignature(bytes: Uint8Array): boolean {
  const lastStart = Math.min(bytes.length - 5, 1_024)
  for (let index = 0; index <= lastStart; index += 1) {
    if (
      bytes[index] === 0x25 &&
      bytes[index + 1] === 0x50 &&
      bytes[index + 2] === 0x44 &&
      bytes[index + 3] === 0x46 &&
      bytes[index + 4] === 0x2d
    ) {
      return true
    }
  }
  return false
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
}

function mapPdfViewerError(
  error: unknown,
  fallback: PdfViewerErrorCode = 'corrupt',
): PdfViewerError {
  if (error instanceof PdfViewerError) return error
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  const candidate = error as { name?: string; message?: string; code?: number }
  if (
    candidate.name === 'PasswordException' ||
    candidate.code === 1 ||
    candidate.code === 2 ||
    /password|encrypted/i.test(candidate.message ?? '')
  ) {
    return new PdfViewerError('encrypted')
  }
  return new PdfViewerError(fallback, candidate.message ?? fallback)
}

async function loadPdfJs(): Promise<PdfJsViewerModule> {
  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsViewerModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
  return pdfjs
}
